import { useEffect, useState } from "react";
import { Activity, AlertTriangle, ChevronDown, Loader2, RefreshCw, Zap, Film, Layers, DownloadCloud } from "lucide-react";
import api from "@/lib/api";

function fmtSeconds(s) {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return r === 0 ? `${m}m` : `${m}m ${r}s`;
}

export default function BunnySnapshotCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/bunny/snapshot");
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not fetch Bunny snapshot");
    } finally {
      setLoading(false);
    }
  };

  const syncIncoming = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const { data } = await api.post("/admin/bunny/sync-incoming");
      setSyncResult(data);
    } catch (e) {
      setSyncResult({ ok: false, reason: e.response?.data?.detail || "Sync failed" });
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { load(); /* on mount */ }, []);

  const warnings = data?.warnings || [];
  const obs = data?.observed || {};
  const times = data?.encoding_times || {};

  return (
    <section
      data-testid="bunny-snapshot-card"
      className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
          <Activity className="w-4 h-4 text-[var(--brand-accent)]" /> Bunny encoder snapshot
        </h2>
        <button
          onClick={load}
          disabled={loading}
          data-testid="bunny-snapshot-refresh"
          className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-[var(--brand-accent)] flex items-center gap-1 transition"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {error ? (
        <div className="text-xs text-red-300 bg-red-950/30 border border-red-900/40 rounded-md p-3">{error}</div>
      ) : !data ? (
        <div className="py-6 flex justify-center text-zinc-600"><Loader2 className="w-4 h-4 animate-spin" /></div>
      ) : (
        <>
          {/* Top-line stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat icon={Film} label="Codecs (latest)" value={obs.codecs?.join(", ") || "—"} />
            <Stat
              icon={Layers}
              label={`Resolutions (latest)${obs.drift_count ? ` · ${obs.drift_count} drift` : ""}`}
              value={obs.resolutions?.length ? obs.resolutions.join(", ") : "—"}
              accent={obs.drift_count ? "amber" : "default"}
            />
            <Stat icon={Zap} label="MP4 fallback" value={obs.mp4_fallback_recent === null ? "—" : obs.mp4_fallback_recent ? "On" : "Off"} />
            <Stat
              icon={Activity}
              label={`Median encode · last ${times.samples ?? 0}`}
              value={times.median_seconds === null ? "no data" : fmtSeconds(times.median_seconds)}
              accent={times.median_seconds && times.median_seconds > 6 * 60 ? "amber" : "default"}
            />
          </div>

          {/* Warnings */}
          {warnings.length > 0 && (
            <div className="space-y-2 mb-4">
              {warnings.map((w, i) => (
                <div
                  key={i}
                  data-testid={`bunny-snapshot-warning-${i}`}
                  className="flex items-start gap-2 text-[11px] text-amber-200 bg-amber-950/20 border border-amber-900/40 rounded-md px-3 py-2 leading-relaxed"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          {warnings.length === 0 && (
            <div className="text-[11px] text-emerald-300 bg-emerald-950/20 border border-emerald-900/40 rounded-md px-3 py-2 mb-4">
              ✓ Encoder config looks healthy.
            </div>
          )}

          {/* Sample details */}
          <button
            onClick={() => setExpanded((p) => !p)}
            className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition py-1"
          >
            <span>Recent uploads ({obs.samples?.length || 0})</span>
            <ChevronDown className={`w-3 h-3 transition ${expanded ? "rotate-180" : ""}`} />
          </button>
          {expanded && (
            <div className="mt-2 space-y-1.5">
              {(obs.samples || []).map((s, i) => (
                <div key={i} className="text-[11px] text-zinc-400 bg-black/30 border border-zinc-900 rounded px-3 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <span className="font-medium text-zinc-200 truncate max-w-[60%]" title={s.title}>{s.title}</span>
                  <span className="text-zinc-500">
                    {s.resolutions || "—"} · {s.codecs || "—"}
                    {s.encoding_seconds !== null && <span className="text-[var(--brand-accent)] ml-2">{fmtSeconds(s.encoding_seconds)}</span>}
                  </span>
                </div>
              ))}
              {(!obs.samples || obs.samples.length === 0) && (
                <div className="text-[11px] text-zinc-600 italic">No recent ready assets to sample.</div>
              )}
              {data.library_config_error && (
                <div className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
                  Note: account-level library config not accessible ({data.library_config_error}). Observed values are derived from recent uploads.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const color = accent === "amber" ? "text-[var(--brand-accent)]" : "text-zinc-100";
  return (
    <div className="bg-black/30 border border-zinc-900 rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] text-zinc-500 mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-sm font-medium truncate ${color}`} title={value}>{value}</div>
    </div>
  );
}
